// レイヤーツリーの純粋な問い合わせ関数。Document を変更しない。

import type { Document, Layer, LayerId, RasterLayer } from '@/types/document'

/** 描画順 (最下層 → 最上層) にすべてのレイヤー ID を平坦化する。グループ自身も含む */
export function flattenLayerIds(doc: Document, children: LayerId[] = doc.rootChildren): LayerId[] {
  const out: LayerId[] = []
  for (const id of children) {
    const layer = doc.layers[id]
    if (!layer) continue
    if (layer.kind === 'group') out.push(...flattenLayerIds(doc, layer.children))
    out.push(id)
  }
  return out
}

/** 親グループ ID。ルート直下なら null、存在しなければ undefined */
export function findParentId(doc: Document, id: LayerId): LayerId | null | undefined {
  if (doc.rootChildren.includes(id)) return null
  for (const layer of Object.values(doc.layers)) {
    if (layer.kind === 'group' && layer.children.includes(id)) return layer.id
  }
  return undefined
}

/** 親の children 配列 (ルートなら rootChildren)。存在しなければ undefined */
export function siblingsOf(doc: Document, id: LayerId): LayerId[] | undefined {
  const parent = findParentId(doc, id)
  if (parent === undefined) return undefined
  if (parent === null) return doc.rootChildren
  const p = doc.layers[parent]
  return p && p.kind === 'group' ? p.children : undefined
}

/** 先祖のどれかが非表示なら false */
export function isEffectivelyVisible(doc: Document, id: LayerId): boolean {
  let cur: LayerId | null | undefined = id
  while (cur) {
    const layer: Layer | undefined = doc.layers[cur]
    if (!layer || !layer.visible) return false
    cur = findParentId(doc, cur)
  }
  return true
}

export function activeLayer(doc: Document): Layer | undefined {
  const id = doc.activeLayerIds[0]
  return id ? doc.layers[id] : undefined
}

export function activeRasterLayer(doc: Document): RasterLayer | undefined {
  const l = activeLayer(doc)
  return l && l.kind === 'raster' ? l : undefined
}

/** パネル表示順 (最上層が先頭) の行リスト。深さ付き */
export interface LayerRow {
  id: LayerId
  depth: number
  layer: Layer
}

export function layerRows(doc: Document, children: LayerId[] = doc.rootChildren, depth = 0): LayerRow[] {
  const out: LayerRow[] = []
  for (let i = children.length - 1; i >= 0; i--) {
    const id = children[i]!
    const layer = doc.layers[id]
    if (!layer) continue
    out.push({ id, depth, layer })
    if (layer.kind === 'group' && layer.expanded) out.push(...layerRows(doc, layer.children, depth + 1))
  }
  return out
}

/** Document から到達可能な pixelId の集合 (レイヤー・マスク・選択範囲) */
export function referencedPixelIds(doc: Document): Set<string> {
  const ids = new Set<string>()
  for (const layer of Object.values(doc.layers)) {
    if (layer.kind === 'raster') ids.add(layer.pixelId)
    if (layer.kind === 'smart') ids.add(layer.sourceId)
    if (layer.kind === 'adjustment' && layer.adjustment.kind === 'color-lookup' && layer.adjustment.lutPixelId) ids.add(layer.adjustment.lutPixelId)
    if (layer.mask) ids.add(layer.mask.pixelId)
  }
  if (doc.selection.pixelId) ids.add(doc.selection.pixelId)
  for (const c of doc.channels ?? []) ids.add(c.pixelId)
  for (const p of doc.patterns ?? []) ids.add(p.pixelId)
  return ids
}

/** 新しいレイヤー名 (「レイヤー 1」「レイヤー 2」…) */
export function nextLayerName(doc: Document, base = 'レイヤー'): string {
  const names = new Set(Object.values(doc.layers).map(l => l.name))
  for (let i = 1; ; i++) {
    const n = `${base} ${i}`
    if (!names.has(n)) return n
  }
}
